import type { STANDARD_ERROR_CALCULATOR_STRINGS } from "@/components/tools/standard-error-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import {
  mean,
  standardDeviation,
  sum,
  sumOfSquares,
} from "@/lib/stats/descriptive";
import { parseProbability } from "@/lib/tool-kit/stats-descriptive/inputs";
import {
  standardErrorOfMean,
  standardErrorOfMeanDifference,
  standardErrorOfProportion,
  standardErrorOfProportionDifference,
} from "@/lib/tool-kit/stats-descriptive/standard-error";

/*
 * The standard-error widget's computations: read one mode's fields, compute, and
 * describe the result as cards and working lines. Every word comes from the widget's
 * strings; this module only decides which numbers go where.
 */

type Strings = typeof STANDARD_ERROR_CALCULATOR_STRINGS;
export type Mode = "mean" | "proportion" | "twoMeans" | "twoProportions";
export type Source = "summary" | "data";
export type FieldKey =
  | "s"
  | "n"
  | "data"
  | "p"
  | "np"
  | "m1"
  | "s1"
  | "n1"
  | "m2"
  | "s2"
  | "n2"
  | "p1"
  | "np1"
  | "p2"
  | "np2";
export type Fields = Record<FieldKey, string>;

export const EMPTY_FIELDS: Fields = {
  s: "",
  n: "",
  data: "",
  p: "",
  np: "",
  m1: "",
  s1: "",
  n1: "",
  m2: "",
  s2: "",
  n2: "",
  p1: "",
  np1: "",
  p2: "",
  np2: "",
};

export interface View {
  cards: { label: string; value: string; emphasis?: boolean }[];
  working: string[];
  note: string;
}

function sampleSize(raw: string): number | null {
  const n = parseIntegerInput(raw);
  return n !== null && n >= 2 ? n : null;
}

function nonNegative(raw: string): number | null {
  const value = parseNumberInput(raw);
  return value !== null && value >= 0 ? value : null;
}

function meanView(
  s: number,
  n: number,
  strings: Strings,
  num: (value: number) => string
): View {
  const se = standardErrorOfMean(s, n);
  return {
    cards: [
      { label: strings.standardError, value: num(se), emphasis: true },
      { label: strings.sampleSd, value: num(s) },
      { label: strings.sampleSize, value: num(n) },
    ],
    working: [
      t(strings.meanWorking, {
        s: num(s),
        n: num(n),
        root: num(Math.sqrt(n)),
        se: num(se),
      }),
    ],
    note: strings.meanNote,
  };
}

function rawView(
  values: number[],
  strings: Strings,
  num: (value: number) => string
): View | null {
  const n = values.length;
  if (n < 2) {
    return null;
  }
  const s = standardDeviation(values);
  const view = meanView(s, n, strings, num);
  const m = mean(values);
  view.cards.splice(1, 0, { label: strings.sampleMean, value: num(m) });
  view.working.unshift(
    t(strings.rawMeanWorking, {
      sum: num(sum(values)),
      n: num(n),
      mean: num(m),
    }),
    t(strings.rawSdWorking, {
      ss: num(sumOfSquares(values)),
      df: num(n - 1),
      s: num(s),
    })
  );
  return view;
}

function proportionView(
  fields: Fields,
  strings: Strings,
  num: (value: number) => string
): View | null {
  const p = parseProbability(fields.p, false);
  const n = sampleSize(fields.np);
  if (p === null || n === null) {
    return null;
  }
  const se = standardErrorOfProportion(p, n);
  return {
    cards: [
      { label: strings.standardError, value: num(se), emphasis: true },
      { label: strings.proportion, value: num(p) },
      { label: strings.sampleSize, value: num(n) },
    ],
    working: [
      t(strings.proportionWorking, {
        p: num(p),
        q: num(1 - p),
        n: num(n),
        inner: num((p * (1 - p)) / n),
        se: num(se),
      }),
    ],
    note: strings.meanNote,
  };
}

function twoMeansView(
  fields: Fields,
  strings: Strings,
  num: (value: number) => string
): View | null {
  const s1 = nonNegative(fields.s1);
  const s2 = nonNegative(fields.s2);
  const n1 = sampleSize(fields.n1);
  const n2 = sampleSize(fields.n2);
  if (s1 === null || s2 === null || n1 === null || n2 === null) {
    return null;
  }
  const se = standardErrorOfMeanDifference(s1, n1, s2, n2);
  const shared = { s1: num(s1), s2: num(s2), n1: num(n1), n2: num(n2) };
  const view: View = {
    cards: [
      { label: strings.unpooled, value: num(se.unpooled), emphasis: true },
      { label: strings.pooled, value: num(se.pooled) },
      { label: strings.pooledSd, value: num(se.pooledSd) },
    ],
    working: [
      t(strings.welchWorking, {
        ...shared,
        a: num((s1 * s1) / n1),
        b: num((s2 * s2) / n2),
        se: num(se.unpooled),
      }),
      t(strings.pooledSdWorking, {
        n1m: num(n1 - 1),
        n2m: num(n2 - 1),
        v1: num(s1 * s1),
        v2: num(s2 * s2),
        df: num(n1 + n2 - 2),
        sp: num(se.pooledSd),
      }),
      t(strings.pooledWorking, {
        ...shared,
        sp: num(se.pooledSd),
        se: num(se.pooled),
      }),
    ],
    note: strings.welchNote,
  };
  const m1 = parseNumberInput(fields.m1);
  const m2 = parseNumberInput(fields.m2);
  if (m1 !== null && m2 !== null) {
    view.cards.push({ label: strings.difference, value: num(m1 - m2) });
    view.working.unshift(
      t(strings.differenceWorking, {
        m1: num(m1),
        m2: num(m2),
        diff: num(m1 - m2),
      })
    );
  }
  return view;
}

function twoProportionsView(
  fields: Fields,
  strings: Strings,
  num: (value: number) => string
): View | null {
  const p1 = parseProbability(fields.p1, false);
  const p2 = parseProbability(fields.p2, false);
  const n1 = sampleSize(fields.np1);
  const n2 = sampleSize(fields.np2);
  if (p1 === null || p2 === null || n1 === null || n2 === null) {
    return null;
  }
  const se = standardErrorOfProportionDifference(p1, n1, p2, n2);
  const pbar = se.pooledProportion;
  return {
    cards: [
      {
        label: strings.unpooledProportion,
        value: num(se.unpooled),
        emphasis: true,
      },
      { label: strings.pooledProportion, value: num(se.pooled) },
      { label: strings.difference, value: num(p1 - p2) },
      { label: strings.pooledP, value: num(pbar) },
    ],
    working: [
      t(strings.propDifferenceWorking, {
        p1: num(p1),
        p2: num(p2),
        diff: num(p1 - p2),
      }),
      t(strings.propUnpooledWorking, {
        a: num((p1 * (1 - p1)) / n1),
        b: num((p2 * (1 - p2)) / n2),
        se: num(se.unpooled),
      }),
      t(strings.pooledPWorking, {
        x1: num(p1 * n1),
        x2: num(p2 * n2),
        nn: num(n1 + n2),
        pbar: num(pbar),
      }),
      t(strings.propPooledWorking, {
        pbar: num(pbar),
        qbar: num(1 - pbar),
        sumInv: num(1 / n1 + 1 / n2),
        se: num(se.pooled),
      }),
    ],
    note: strings.proportionNote,
  };
}

export function computeView(
  mode: Mode,
  source: Source,
  fields: Fields,
  strings: Strings,
  num: (value: number) => string
): View | null {
  if (mode === "proportion") {
    return proportionView(fields, strings, num);
  }
  if (mode === "twoMeans") {
    return twoMeansView(fields, strings, num);
  }
  if (mode === "twoProportions") {
    return twoProportionsView(fields, strings, num);
  }
  if (source === "data") {
    return rawView(parseNumberList(fields.data), strings, num);
  }
  const s = nonNegative(fields.s);
  const n = sampleSize(fields.n);
  return s === null || n === null ? null : meanView(s, n, strings, num);
}

export const EXAMPLE_KEYS: Record<FieldKey, keyof Strings> = {
  s: "exampleS",
  n: "exampleN",
  data: "exampleData",
  p: "exampleP",
  np: "exampleNp",
  m1: "exampleM1",
  s1: "exampleS1",
  n1: "exampleN1",
  m2: "exampleM2",
  s2: "exampleS2",
  n2: "exampleN2",
  p1: "exampleP1",
  np1: "exampleNp1",
  p2: "exampleP2",
  np2: "exampleNp2",
};
